/* Kashira wishlist v2 — device-local (localStorage), guest-friendly.
   Storage layer is isolated in KashiraWishlistStore so a synced backend
   (e.g. Swym) can replace it later without touching the UI elements.

   Two keys:
     kashira:wishlist       — ordered array of product handles (source of truth)
     kashira:wishlist:meta  — { handle: { savedAt, price } } recorded on this
                              device at save time. Powers "Saved 12 Jul" and the
                              price-drop line. Purely local; nothing is invented.

   Elements:
     <kashira-wishlist-toggle data-handle="..."> — heart toggle (PDP/cards)
     <kashira-wishlist-count>                    — header badge
     <kashira-wishlist-grid>                     — wishlist page renderer
     <kashira-wishlist-suggestions>              — recommendations row
*/

/* Re-entry guard. The module is loaded once globally (layout/theme.liquid),
   but the quick-add modal injects product markup that can re-run this file;
   a second pass would hit `const KashiraWishlistStore` again and throw
   "Identifier has already been declared", aborting the script. Wrapping in an
   IIFE that bails when the store already exists keeps the console clean and
   makes a re-run a no-op rather than an error.

   Intentionally NOT keyed on a bespoke window flag: product-info.js swaps the
   whole <product-info> on variant change, and the custom elements below must
   still upgrade for freshly injected nodes. customElements.get() already
   guards each definition, so re-entry is safe once the const is scoped. */
(function () {
  if (window.KashiraWishlistStore) return;

  const KashiraWishlistStore = {
    KEY: 'kashira:wishlist',
    META_KEY: 'kashira:wishlist:meta',

    list() {
      try {
        return JSON.parse(localStorage.getItem(this.KEY)) || [];
      } catch {
        return [];
      }
    },

    meta() {
      try {
        return JSON.parse(localStorage.getItem(this.META_KEY)) || {};
      } catch {
        return {};
      }
    },

    metaFor(handle) {
      return this.meta()[handle] || null;
    },

    has(handle) {
      return this.list().includes(handle);
    },

    /* meta: optional { price } captured at save time (minor units) */
    toggle(handle, info = {}) {
      const items = this.list();
      const meta = this.meta();
      const i = items.indexOf(handle);
      const added = i === -1;

      if (added) {
        items.unshift(handle);
        meta[handle] = {
          savedAt: Date.now(),
          price: Number.isFinite(Number(info.price)) ? Number(info.price) : null,
        };
      } else {
        items.splice(i, 1);
        delete meta[handle];
      }

      const kept = items.slice(0, 100);
      Object.keys(meta).forEach((key) => {
        if (!kept.includes(key)) delete meta[key];
      });

      try {
        localStorage.setItem(this.KEY, JSON.stringify(kept));
        localStorage.setItem(this.META_KEY, JSON.stringify(meta));
      } catch {
        /* storage full or blocked (private mode) — UI still reflects the session */
      }

      document.dispatchEvent(new CustomEvent('kashira:wishlist:change', { detail: { items: kept } }));
      return added;
    },

    /* Restore a removed item at its original position — used by the UNDO toast. */
    restore(handle, index, info) {
      const items = this.list().filter((h) => h !== handle);
      items.splice(Math.max(0, Math.min(index, items.length)), 0, handle);
      const meta = this.meta();
      if (info) meta[handle] = info;

      try {
        localStorage.setItem(this.KEY, JSON.stringify(items.slice(0, 100)));
        localStorage.setItem(this.META_KEY, JSON.stringify(meta));
      } catch {
        /* ignore */
      }

      document.dispatchEvent(new CustomEvent('kashira:wishlist:change', { detail: { items } }));
    },
  };
  window.KashiraWishlistStore = KashiraWishlistStore;

  const kashiraReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Cross-tab sync: another tab wrote to the wishlist keys. */
  window.addEventListener('storage', (event) => {
    if (event.key === KashiraWishlistStore.KEY) {
      document.dispatchEvent(
        new CustomEvent('kashira:wishlist:change', { detail: { items: KashiraWishlistStore.list(), external: true } })
      );
    }
  });

  if (!customElements.get('kashira-wishlist-toggle')) {
    customElements.define(
      'kashira-wishlist-toggle',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.handle = this.dataset.handle;
          if (!this.button) return;
          this.button.addEventListener('click', () => {
            KashiraWishlistStore.toggle(this.handle, { price: this.dataset.price });
            this.render();
          });
          document.addEventListener('kashira:wishlist:change', () => this.render());
          this.render();
        }

        render() {
          const active = KashiraWishlistStore.has(this.handle);
          this.button.setAttribute('aria-pressed', active);
          this.button.classList.toggle('kashira-wishlist-toggle--active', active);
          const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
          if (label) this.button.setAttribute('aria-label', label);
          const labelEl = this.button.querySelector('.kashira-wishlist-toggle__label');
          if (labelEl && label) labelEl.textContent = label;
        }
      }
    );
  }

  if (!customElements.get('kashira-wishlist-count')) {
    customElements.define(
      'kashira-wishlist-count',
      class extends HTMLElement {
        connectedCallback() {
          document.addEventListener('kashira:wishlist:change', () => this.render());
          this.render();
        }

        render() {
          const n = KashiraWishlistStore.list().length;
          this.textContent = n > 0 ? n : '';
          this.classList.toggle('kashira-wishlist-count--empty', n === 0);

          /* Keep the header link's accessible name in sync with the count so
             screen readers announce "Wishlist, 3 saved" (the numeral itself is
             aria-hidden). Templates come from data attributes on the link. */
          const link = this.closest('a');
          if (!link) return;
          const template = n > 0 ? link.dataset.labelCount : link.dataset.labelEmpty;
          if (template) link.setAttribute('aria-label', n > 0 ? template.replace('[count]', n) : template);
        }
      }
    );
  }

  if (!customElements.get('kashira-wishlist-grid')) {
    customElements.define(
      'kashira-wishlist-grid',
      class extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-kashira-wishlist-list]');
          this.emptyEl = this.querySelector('[data-kashira-wishlist-empty]');
          this.loadingEl = this.querySelector('[data-kashira-wishlist-loading]');
          this.errorEl = this.querySelector('[data-kashira-wishlist-error]');
          this.countLabel = document.querySelector('[data-kashira-wishlist-count-label]');
          this.shareBtn = document.querySelector('[data-kashira-wishlist-share]');
          this.sharedNote = document.querySelector('[data-kashira-wishlist-shared-note]');
          this.rendered = [];

          /* A shared list travels in the URL — there is no wishlist server, so
             SHARE encodes the handles and this view renders them read-only. */
          const shared = new URLSearchParams(window.location.search).get('items');
          this.sharedHandles = shared ? shared.split(',').filter(Boolean).slice(0, 50) : null;
          if (this.sharedHandles) {
            this.classList.add('kashira-wishlist__grid-wrap--shared');
            if (this.sharedNote) this.sharedNote.hidden = false;
          }

          if (this.shareBtn) this.shareBtn.addEventListener('click', () => this.share());
          this.setupNotice();

          this.addEventListener('click', (event) => {
            const remove = event.target.closest('[data-kashira-wishlist-remove]');
            if (remove) this.removeItem(remove.dataset.handle);
          });

          /* Saved is not the same as in-cart: keep the item after add-to-cart
             and morph the button, matching the 17 System Feedback contract. */
          this.addEventListener('submit', (event) => {
            const form = event.target.closest('form[data-type="add-to-cart-form"]');
            if (form) this.morphAddButton(form);
          });

          document.addEventListener('kashira:wishlist:change', (event) => {
            if (this.sharedHandles) return;
            this.render();
          });

          this.render();
        }

        morphAddButton(form) {
          const button = form.querySelector('button[type="submit"] span');
          if (!button || !this.dataset.addedLabel) return;
          const original = button.textContent;
          window.setTimeout(() => {
            button.textContent = this.dataset.addedLabel;
            window.setTimeout(() => {
              button.textContent = original;
            }, 1200);
          }, 300);
        }

        setCount(n) {
          if (this.shareBtn) this.shareBtn.hidden = n === 0 || Boolean(this.sharedHandles);
          /* The device notice only earns its space once something is saved. */
          if (this.noticeEl && !this.noticeDismissed()) this.noticeEl.hidden = n === 0 || Boolean(this.sharedHandles);
          if (!this.countLabel) return;
          const template = n === 1 ? this.dataset.countOne : this.dataset.countOther;
          this.countLabel.textContent = n > 0 && template ? template.replace('[count]', n) : '';
        }

        noticeDismissed() {
          try {
            return localStorage.getItem('kashira:wishlist:notice') === 'dismissed';
          } catch {
            return false;
          }
        }

        setupNotice() {
          this.noticeEl = document.querySelector('[data-kashira-wishlist-notice]');
          const dismiss = document.querySelector('[data-kashira-wishlist-notice-dismiss]');
          if (!this.noticeEl || !dismiss) return;
          if (this.noticeDismissed()) this.noticeEl.remove();
          dismiss.addEventListener('click', () => {
            this.noticeEl.hidden = true;
            try {
              localStorage.setItem('kashira:wishlist:notice', 'dismissed');
            } catch {
              /* private mode — the notice simply returns next visit */
            }
          });
        }

        async share() {
          const handles = KashiraWishlistStore.list();
          if (!handles.length) return;
          const url = new URL(window.location.href);
          url.searchParams.set('items', handles.join(','));
          try {
            await navigator.clipboard.writeText(url.toString());
            if (window.KashiraToast) {
              window.KashiraToast.show(this.shareBtn.dataset.copied, { variant: 'success' });
            }
          } catch {
            /* A copy failure must not read like the success it replaces. */
            if (window.KashiraToast) {
              window.KashiraToast.show(this.shareBtn.dataset.failed, { variant: 'warning' });
            }
          }
        }

        async render() {
          const handles = this.sharedHandles || KashiraWishlistStore.list();
          this.setCount(handles.length);

          if (!handles.length) {
            this.rendered = [];
            if (this.list) {
              this.list.innerHTML = '';
              this.list.hidden = true;
            }
            if (this.loadingEl) this.loadingEl.hidden = true;
            if (this.errorEl) this.errorEl.hidden = true;
            if (this.emptyEl) this.emptyEl.hidden = false;
            document.dispatchEvent(new CustomEvent('kashira:wishlist:seed', { detail: { productId: null } }));
            return;
          }

          /* Only fetch cards we do not already have on screen. */
          const same = handles.length === this.rendered.length && handles.every((h, i) => h === this.rendered[i]);
          if (same) {
            this.decorate();
            return;
          }

          if (this.emptyEl) this.emptyEl.hidden = true;
          if (this.errorEl) this.errorEl.hidden = true;
          if (this.loadingEl && !this.rendered.length) this.loadingEl.hidden = false;

          const cards = await Promise.all(
            handles.map((handle) =>
              fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?view=card`)
                .then((response) => (response.ok ? response.text() : null))
                .catch(() => null)
            )
          );

          const html = cards.filter(Boolean).join('');
          if (this.loadingEl) this.loadingEl.hidden = true;

          if (!html) {
            if (this.list) this.list.hidden = true;
            if (this.errorEl) this.errorEl.hidden = false;
            return;
          }

          if (this.list) {
            this.list.innerHTML = html;
            this.list.hidden = false;
            this.appendBrowseTile();
          }
          this.rendered = handles.slice();
          this.decorate();
          this.seedSuggestions();
        }

        /* Ref 05 L334-337 (desktop) / L667-670 (mobile): a dashed tile closes the
           grid, so a list of 1, 2, 5, 6 or 7 items does not end on a ragged row
           with no way out of it. Built node by node — the label is merchant copy
           and must not be re-parsed as markup. */
        appendBrowseTile() {
          const { browseUrl, browseLabel } = this.dataset;
          if (!browseUrl || !browseLabel) return;

          const tile = document.createElement('li');
          tile.className = 'kashira-saved-card kashira-saved-card--browse';

          const link = document.createElement('a');
          link.className = 'kashira-saved-card__browse-link';
          link.href = browseUrl;

          const mark = document.createElement('span');
          mark.className = 'kashira-saved-card__browse-mark';
          mark.setAttribute('aria-hidden', 'true');
          mark.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M12 5v14M5 12h14"/></svg>';

          const label = document.createElement('span');
          label.className = 'kashira-saved-card__browse-label';
          label.textContent = browseLabel;

          link.append(mark, label);
          tile.append(link);
          this.list.append(tile);
        }

        /* Fill the two device-local lines: saved date and price drop. */
        decorate() {
          /* Save dates and price drops are this device's data — never applied to
             someone else's shared list. */
          if (!this.list || this.sharedHandles) return;
          /* Day-first, per the store's locale. `document.documentElement.lang`
             is bare "en", which Intl reads as en-US and renders month-first —
             "Aug 13" where every other date on the storefront reads "13 Aug".
             Regionless "en" is the trap here, not the browser. */
          const dateFormat = new Intl.DateTimeFormat('en-IN', {
            day: 'numeric',
            month: 'short',
          });

          this.list.querySelectorAll('.kashira-saved-card').forEach((card) => {
            const info = KashiraWishlistStore.metaFor(card.dataset.handle);
            if (!info) return;

            const savedEl = card.querySelector('[data-kashira-saved-on]');
            if (savedEl && info.savedAt && this.dataset.savedOn) {
              savedEl.textContent = this.dataset.savedOn.replace('[date]', dateFormat.format(new Date(info.savedAt)));
              savedEl.hidden = false;
            }

            const now = Number(card.dataset.price);
            const dropEl = card.querySelector('[data-kashira-drop]');
            const dropText = card.querySelector('[data-kashira-drop-text]');
            if (dropEl && dropText && this.dataset.dropText && Number.isFinite(now) && info.price > now) {
              const delta = info.price - now;
              dropText.textContent = this.dataset.dropText.replace('[amount]', this.formatMoney(delta));
              dropEl.hidden = false;
            }
          });
        }

        formatMoney(minorUnits) {
          const format = window.Shopify?.currency?.active || 'INR';
          try {
            return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
              style: 'currency',
              currency: format,
              maximumFractionDigits: 0,
            }).format(minorUnits / 100);
          } catch {
            return (minorUnits / 100).toFixed(0);
          }
        }

        seedSuggestions() {
          const first = this.list && this.list.querySelector('.kashira-saved-card');
          document.dispatchEvent(
            new CustomEvent('kashira:wishlist:seed', {
              detail: { productId: first ? first.dataset.productId : null },
            })
          );
        }

        /* Collapse the card, reflow the grid with FLIP, offer UNDO for 5s. */
        removeItem(handle) {
          if (!handle || !this.list) return;
          const card = this.list.querySelector(`.kashira-saved-card[data-handle="${CSS.escape(handle)}"]`);
          const index = KashiraWishlistStore.list().indexOf(handle);
          const info = KashiraWishlistStore.metaFor(handle);
          const title = card ? card.dataset.title : handle;

          const commit = () => {
            KashiraWishlistStore.toggle(handle);
            if (window.KashiraToast && this.dataset.removeToast) {
              window.KashiraToast.show(this.dataset.removeToast.replace('[title]', title), {
                actionLabel: this.dataset.undo,
                onAction: () => KashiraWishlistStore.restore(handle, index, info),
              });
            }
          };

          if (!card || kashiraReducedMotion()) {
            commit();
            return;
          }

          const others = Array.from(this.list.querySelectorAll('.kashira-saved-card')).filter((el) => el !== card);
          const before = new Map(others.map((el) => [el, el.getBoundingClientRect()]));

          card.classList.add('kashira-saved-card--leaving');
          card.addEventListener(
            'animationend',
            () => {
              card.remove();
              this.rendered = this.rendered.filter((h) => h !== handle);

              others.forEach((el) => {
                const start = before.get(el);
                const end = el.getBoundingClientRect();
                const dx = start.left - end.left;
                const dy = start.top - end.top;
                if (!dx && !dy) return;
                el.animate(
                  [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
                  { duration: 300, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
                );
              });

              commit();
            },
            { once: true }
          );
        }
      }
    );
  }

  if (!customElements.get('kashira-wishlist-suggestions')) {
    customElements.define(
      'kashira-wishlist-suggestions',
      class extends HTMLElement {
        connectedCallback() {
          /* When the recommendations endpoint re-renders this section the
             returned markup already carries data-performed="true". */
          if (this.dataset.performed === 'true') return;
          document.addEventListener('kashira:wishlist:seed', (event) => this.load(event.detail.productId));
        }

        async load(productId) {
          if (!productId || productId === this.loadedFor || !this.dataset.url) return;
          this.loadedFor = productId;
          try {
            const response = await fetch(`${this.dataset.url}&product_id=${productId}`);
            if (!response.ok) return;
            const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
            const fresh = markup.querySelector('kashira-wishlist-suggestions');
            if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          } catch {
            /* keep the merchant fallback row on screen */
          }
        }
      }
    );
  }
})();
